import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { clsx } from '../clsx';
import { useEscapeKey, useFocusTrap, useIsMobile, useStableId } from '../hooks';
import { Eyebrow } from './Eyebrow';
import { Icon } from './Icon';
import { Overlay } from './Overlay';
import { Sheet, lockBodyScroll } from './Sheet';
import styles from './Dialog.module.css';

export type DialogTone = 'success' | 'danger' | 'warning' | 'info';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: ReactNode | undefined;
  subtitle?: ReactNode | undefined;
  /** 600 (forms), 560 (confirmations). */
  width?: number | undefined;
  /** Coloured compact header of the stage-change dialogs (Deal-Pipeline 17–22): dot + title + close cross. */
  tone?: DialogTone | undefined;
  /** Right-aligned actions of the footer (`--sand-50` bar). */
  footer?: ReactNode | undefined;
  /** Note at the start of the footer ("Legen Sie trotzdem an, bekommt der Admin einen Hinweis."). */
  footerNote?: ReactNode | undefined;
  /** Top edge of the scrim: 60 keeps the topbar visible. */
  overlayTop?: number | undefined;
  closeOnScrim?: boolean | undefined;
  closeOnEscape?: boolean | undefined;
  initialFocus?: RefObject<HTMLElement | null> | 'first' | 'container' | 'none' | undefined;
  describedBy?: string | undefined;
  /** Renders as a bottom sheet below 768 px (default true). */
  sheetOnMobile?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
  bodyClassName?: string | undefined;
  /** Positioned frame to render into instead of `document.body` (the overlay then covers only that frame). */
  container?: HTMLElement | null | undefined;
  children: ReactNode;
}

/** Modal dialog of the screens: scrim below the topbar, `--radius-sheet` card, eyebrow + display title, sand footer. */
export function Dialog({
  open,
  onClose,
  title,
  eyebrow,
  subtitle,
  width = 600,
  tone,
  footer,
  footerNote,
  overlayTop = 60,
  closeOnScrim = true,
  closeOnEscape = true,
  initialFocus = 'first',
  describedBy,
  sheetOnMobile = true,
  id,
  className,
  bodyClassName,
  container,
  children,
}: DialogProps) {
  const mobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const dialogId = useStableId(id);
  const titleId = `${dialogId}-title`;
  const asSheet = mobile && sheetOnMobile;
  const active = open && !asSheet;
  useEscapeKey(closeOnEscape ? onClose : undefined, active);
  useFocusTrap(panelRef, active, { initialFocus });
  useEffect(() => (active && !container ? lockBodyScroll() : undefined), [active, container]);

  if (!open) return null;
  if (asSheet) {
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title={title}
        eyebrow={eyebrow}
        subtitle={subtitle}
        footer={footer}
        footerNote={footerNote}
        closeOnScrim={closeOnScrim}
        closeOnEscape={closeOnEscape}
        initialFocus={initialFocus}
        id={id}
        container={container}
      >
        {children}
      </Sheet>
    );
  }

  return createPortal(
    <Overlay top={overlayTop} onScrimClick={closeOnScrim ? onClose : undefined} absolute={Boolean(container)}>
      <div
        ref={panelRef}
        id={dialogId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={clsx(styles.dialog, tone && styles.toned, tone && styles[tone], className)}
        style={{ width }}
      >
        {tone ? (
          <div className={styles.toneHeader}>
            <span className={styles.toneDot} aria-hidden="true" />
            <div className={styles.toneText}>
              <div id={titleId} className={styles.toneTitle}>
                {title}
              </div>
              {subtitle ? <div className={styles.toneSubtitle}>{subtitle}</div> : null}
            </div>
            <button type="button" aria-label="Schließen" className={styles.toneClose} onClick={onClose}>
              <Icon icon={X} size={16} />
            </button>
          </div>
        ) : (
          <div className={styles.header}>
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            <div id={titleId} className={clsx(styles.title, Boolean(eyebrow) && styles.titleAfterEyebrow)}>
              {title}
            </div>
            {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
          </div>
        )}
        <div className={clsx(styles.body, bodyClassName)}>{children}</div>
        {footer || footerNote ? (
          <div className={styles.footer}>
            {footerNote ? <span className={styles.footerNote}>{footerNote}</span> : null}
            {footer ? <span className={styles.footerActions}>{footer}</span> : null}
          </div>
        ) : null}
      </div>
    </Overlay>,
    container ?? document.body,
  );
}
